/*******************************************************************************
    plik  : frontend/src/pages/ProgressPage.tsx
    autor : Michal Kolodziejski (2:480/112.10)
    data  : 2025.01.06
    copyright  : PTODT <https://ptodt.org.pl>

    opis  : Progress page showing conversion status and progress

    licencja : MIT
*******************************************************************************/

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJobStatus, formatFileSize, formatDuration } from '../services/api';
import FastCopyProgress from '../components/FastCopyProgress';
import type { ConversionJob } from '../types';

/**
 * @brief Progress page component
 * @return Progress page JSX element
 */
const ProgressPage: React.FC = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const [job, setJob] = useState<ConversionJob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [elapsedTime, setElapsedTime] = useState(0);

    /**
     * @brief Fetches job status periodically
     */
    useEffect(() => {
        if (!jobId) {
            setError('Brak identyfikatora zadania');
            return;
        }

        let isMounted = true;
        let pollInterval: ReturnType<typeof setInterval>;

        const fetchStatus = async () => {
            try {
                const jobData = await getJobStatus(jobId);

                if (!isMounted) return;

                setJob(jobData);
                setError(null);

                /** @brief Redirect based on status */
                if (jobData.status === 'completed') {
                    navigate(`/success/${jobId}`);
                } else if (jobData.status === 'error') {
                    setError(jobData.errorMessage || 'Wystąpił błąd podczas konwersji');
                }
            } catch (err) {
                if (!isMounted) return;
                setError(err instanceof Error ? err.message : 'Nie można pobrać statusu zadania');
            }
        };

        fetchStatus();
        pollInterval = setInterval(fetchStatus, 1000);

        return () => {
            isMounted = false;
            clearInterval(pollInterval);
        };
    }, [jobId, navigate]);

    /**
     * @brief Updates elapsed time counter
     */
    useEffect(() => {
        if (!job?.startedAt) return;

        const updateTime = () => {
            setElapsedTime(Date.now() - job.startedAt!);
        };

        updateTime();
        const interval = setInterval(updateTime, 1000);

        return () => clearInterval(interval);
    }, [job?.startedAt]);

    /**
     * @brief Handles returning to upload page
     */
    const handleBack = useCallback(() => {
        navigate('/');
    }, [navigate]);

    /** @brief Show loading state */
    if (!job && !error) {
        return (
            <div className="progress-page">
                <div className="loading" role="status" aria-live="polite">
                    <div className="loading__spinner" aria-hidden="true"></div>
                    <p className="loading__text">Ładowanie...</p>
                </div>
            </div>
        );
    }

    /** @brief Show error state */
    if (error) {
        return (
            <div className="progress-page">
                <div className="error-container" role="alert">
                    <h1 className="error-container__title">Błąd</h1>
                    <p className="error-container__message">{error}</p>
                    <button
                        type="button"
                        className="error-container__button"
                        onClick={handleBack}
                    >
                        Powrót do strony głównej
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="progress-page">
            <h1 className="progress-page__title">Konwersja w toku</h1>

            <div className="job-info">
                <div className="job-info__row">
                    <span className="job-info__label">Plik:</span>
                    <span className="job-info__value">{job!.originalFilename}</span>
                </div>
                <div className="job-info__row">
                    <span className="job-info__label">Rozmiar:</span>
                    <span className="job-info__value">{formatFileSize(job!.uploadedFileSize)}</span>
                </div>
                <div className="job-info__row">
                    <span className="job-info__label">Status:</span>
                    <span className={`job-info__status job-info__status--${job!.status}`}>
                        {getStatusText(job!.status)}
                    </span>
                </div>
                {job!.queuePosition > 0 && (
                    <div className="job-info__row">
                        <span className="job-info__label">Pozycja w kolejce:</span>
                        <span className="job-info__value">{job!.queuePosition}</span>
                    </div>
                )}
                {job!.startedAt && (
                    <div className="job-info__row">
                        <span className="job-info__label">Czas trwania:</span>
                        <span className="job-info__value">{formatDuration(elapsedTime)}</span>
                    </div>
                )}
            </div>

            {job!.status === 'processing' && job!.trackProgress.length > 0 && (
                <FastCopyProgress
                    trackProgress={job!.trackProgress}
                    currentTrack={job!.currentTrack}
                    currentSide={job!.currentSide}
                    totalTracks={job!.totalTracks}
                    sides={job!.totalSides}
                    processedSectors={job!.processedSectors}
                    totalSectors={job!.totalSectors}
                />
            )}

            {(job!.status === 'pending' || job!.status === 'queued') && (
                <div className="queue-waiting" role="status" aria-live="polite">
                    <div className="queue-waiting__icon" aria-hidden="true">⏳</div>
                    <p className="queue-waiting__text">
                        Oczekiwanie w kolejce...
                    </p>
                    <p className="queue-waiting__position">
                        Pozycja: {job!.queuePosition} z maksymalnie 5 jednocześnie przetwarzanych
                    </p>
                </div>
            )}

            <button
                type="button"
                className="back-button"
                onClick={handleBack}
            >
                Anuluj i wróć
            </button>
        </div>
    );
};

/**
 * @brief Converts status code to Polish text
 * @param status Job status
 * @return Polish status text
 */
function getStatusText(status: string): string {
    switch (status) {
        case 'pending':
            return 'Oczekuje';
        case 'queued':
            return 'W kolejce';
        case 'processing':
            return 'Przetwarzanie';
        case 'completed':
            return 'Ukończone';
        case 'error':
            return 'Błąd';
        default:
            return status;
    }
}

export default ProgressPage;
